import InputIcon from "@mui/icons-material/Input";
import { SortingState } from "@tanstack/react-table";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import StatusBadge from "Common/Components/StatusBadge/StatusBadge";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import LoadingSpinner from "components/LoadingSpinner";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchWarehouseDropdown } from "slices/Dropdowns/Warehouse/WarehouseDropdown/thunk";
import {
  exportPendingPOCSV,
  fetchPendingPOs,
} from "slices/Warehouse/ItemInward/PendingPO/thunk";
import { resetPendingPOState } from "slices/Warehouse/ItemInward/PendingPO/reducer";
import Tooltip from "@mui/material/Tooltip";
import StatusChip from "pages/Enquiry/Components/StatusChip";

type PendingPOProps = {
  warehouseId: string;
};
const PendingPO: React.FC<PendingPOProps> = ({ warehouseId }) => {
  document.title = "Pending Purchase Orders";

  const tableState = usePreservedTableState({
    key: TableKeys.WAREHOUSE.ITEM_INWARD.PENDING_PO,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [data, setData] = useState([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectPendingPOs = createSelector(
    (state: any) => state.WarehousePendingPO,
    (item) => ({
      list: item.list,
      rowCount: item.rowCount,
      loading: item.loading,
      error: item.error,
    }),
  );

  const { list, rowCount, loading, error } = useSelector(selectPendingPOs);

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.item_inward,
  );

  // Initial Listing Dispatch
  useEffect(() => {
    if (warehouseId) {
      dispatch(
        fetchPendingPOs({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
          warehouse_id: warehouseId,
        }),
      );
    } else {
      dispatch(resetPendingPOState());
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    sortField,
    sortDirection,
    warehouseId,
  ]);

  useEffect(() => {
    setData(list);
  }, [list]);

  // Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    dispatch(fetchWarehouseDropdown());
  }, [dispatch]);


  // Filter menu items to check if any will be visible
  // This ensures we only show the Action column if there are visible menu items
  const DynamicAction = canCreate
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => {
            const data = row.original;
            const menuItems = [
              {
                icon: InputIcon,
                label: "Inward",
                permission: canCreate,
                link: (item: any) =>
                  `/warehouse-item-inward-create-grn/${data.po_id}/${warehouseId}`,
                color: "primary",
              },
            ].filter((item) => item.permission === true);
            return <DynamicMenuItem row={data} menuItems={menuItems} />;
          },
        },
      ]
    : [];

  const columns = [
    // {
    //   header: "Sr No.",
    //   accessorKey: "sr_no",
    //   enableColumnFilter: false,
    //   enableSorting: true,
    // },
    {
      header: "PO No.",
      accessorKey: "po_no",
      enableSorting: true,
      enableColumnFilter: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        API_ENDPOINT?.WAREHOUSE?.ITEM_INWARD?.PENDING_PO?.FILTER_DROPDOWN(
          "po_no",
          warehouseId,
        ),
      cell: ({ row }: { row: any }) => {
        return row.original.po_no || "-";
      },
    },

    {
      header: "PO Date",
      accessorKey: "po_date",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => (
        <FormattedDate date={row.original.po_date} />
      ),
    },
    {
      header: "Supplier",
      accessorKey: "supplier__supplier_name",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        API_ENDPOINT?.WAREHOUSE?.ITEM_INWARD?.PENDING_PO?.FILTER_DROPDOWN(
          "supplier__supplier_name",
          warehouseId,
        ),
      cell: ({ row }: { row: any }) => {
        return (
          row.original?.supplier?.supplier_name ||
          row.original.supplier_name ||
          "-"
        );
      },
    },
    {
      header: "Initiated By",
      accessorKey: "initiated_by",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        API_ENDPOINT?.WAREHOUSE?.ITEM_INWARD?.PENDING_PO?.FILTER_DROPDOWN(
          "initiated_by",
          warehouseId,
        ),
      cell: ({ row }: { row: any }) => {
        return row.original?.initiated_by || "-";
      },
    },
    {
      header: "ETA Date",
      accessorKey: "eta_date",
      enableColumnFilter: true,
      enableSorting: true,
      filterType: "date" as const,
      cell: ({ row }: { row: any }) => (
        <FormattedDate date={row.original.eta_date} />
      ),
    },
    {
      header: "Received Status",
      accessorKey: "received_status",
      enableSorting: true,
      enableColumnFilter: true,
      filterType: "dropdown" as const,
      filterFetchUrl:
        API_ENDPOINT?.WAREHOUSE?.ITEM_INWARD?.PENDING_PO?.FILTER_DROPDOWN(
          "received_status",
          warehouseId,
        ),
      cell: ({ row }: { row: any }) => {
        const status = row.original.received_status;
        return <StatusChip status={status} />;

        // const getStatusColor = (status: string) => {
        //   switch (status?.toLowerCase()) {
        //     case "cancelled":
        //       return "#FF6C6C"; // red
        //     case "partially inwarded":
        //       return "#FBBD4A"; // yellow
        //     case "complete inwarded":
        //       return "#0CBA91"; // green
        //     case "pending":
        //       return "#E89464"; // orange
        //     default:
        //       return "#ccc"; // fallback gray
        //   }
        // };

        // const displayText = status
        //   ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
        //   : "pending";

        // return (
        //   <StatusBadge
        //     status={displayText}
        //     bgColor={status ? getStatusColor(status) : "#FF6C6C"}
        //     color="#fff"
        //   />
        // );
      },
    },
    {
      header: "Notes",
      accessorKey: "Note",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const notes = row.original?.notes || "-";

        return (
          <Tooltip title={notes !== "-" ? notes : ""} arrow placement="top">
            <div
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 4, // 👈 number of lines
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                whiteSpace: "normal",
                wordBreak: "break-word",
                minWidth: "200px",
                cursor: notes !== "-" ? "pointer" : "default",
              }}
            >
              {notes}
            </div>
          </Tooltip>
        );
      },
    },

    ...DynamicAction,
  ];

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action"]}
              />
            )}
            {canView && (
              <ExportButton
                onExport={async () => {
                  return await exportPendingPOCSV({
                    warehouse_id: warehouseId,
                    filters: filterValue,
                    search,
                  });
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <h5>Error: {error}</h5>
          </div>
        ) : (
          <LoaderErrorWrapper loading={loading} error={error}>
            <MasterTableContainer
              columns={columns}
              data={data || []}
              totalRecords={rowCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              colValue={setFilterValue}
              sorting={sorting}
              onSortingChange={setSorting}
              isBordered={false}
              isPagination={true}
              manualSorting={true}
              onTableReady={setTableInstance}
            />
          </LoaderErrorWrapper>
        )}
      </div>
    </div>
  );
};

export default PendingPO;
